
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, MenuItem, InputAdornment, Pagination, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
// import FileDownloadIcon from '@mui/icons-material/FileDownload';
import dayjs, { Dayjs } from 'dayjs';
import CustomDateRangePicker from '../../../../components/CustomDateRangePicker';

import { AppDispatch } from '../../../../store';

import {
  selectFacilityShiftHistory,
  selectFacilityShiftHistoryCount,
  selectFacilityShiftHistoryLoading,
  IShiftHistoryItem,
  fetchFacilityShiftHistory
} from '../../../../modules/facility';
import { fetchShiftDetails } from '../../../../modules/shift';
import {
  TableWrapper,
  FilterContainer,
  SearchField,
  FilterSelect,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StatusChip,
  PaginationContainer,
  // ExportButton
} from './styles';
import { TableBody, Divider } from '@mui/material';
import DetailModal from '../../../../components/DetailModal';

import WorkerAssigned from './components/WorkerAssigned';
import ShiftDetails from './components/ShiftDetails';
import Feedback from './components/Feedback';


const ShiftHistory: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const history = useSelector(selectFacilityShiftHistory);
  const count = useSelector(selectFacilityShiftHistoryCount);
  const loading = useSelector(selectFacilityShiftHistoryLoading);

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [selectedShift, setSelectedShift] = useState<IShiftHistoryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchFacilityShiftHistory({
      page,
      pageSize,
      search: search,
      status: status === '' ? undefined : status.toLowerCase(),
      startDate: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      endDate: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    }));
  }, [dispatch, page, pageSize, search, status, dateRange]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleStatusChange = (event: any) => {
    setStatus(event.target.value);
    setPage(1);
  };

  const calculateDuration = (start: string, end: string) => {
    const s = dayjs(start);
    const e = dayjs(end);
    const diff = e.diff(s, 'hour', true);
    return `${diff.toFixed(2)} hours`;
  };

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>

        {/* <ExportButton variant="contained" disableElevation startIcon={<FileDownloadIcon />}>
          Export as CSV/PDF
        </ExportButton> */}
      </Box>

      <TableWrapper>

        <FilterContainer>
          <SearchField
            placeholder="Search by Facility Name, Location"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        setSearch('');
                        setPage(1);
                      }}
                      edge="end"
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <CustomDateRangePicker
              value={dateRange}
              onChange={(newValue: [Dayjs | null, Dayjs | null]) => {
                setDateRange(newValue);
                setPage(1);
              }}
            />

            <FilterSelect
              value={status}
              onChange={handleStatusChange}
              displayEmpty
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="partially_assigned">Partially Assigned</MenuItem>
              <MenuItem value="partially_started">Partially Started</MenuItem>
              <MenuItem value="started">Started</MenuItem>
              <MenuItem value="partially_completed">Partially Completed</MenuItem>
              <MenuItem value="partially_approval_pending">Partially Approval Pending</MenuItem>
              <MenuItem value="approval_pending">Approval Pending</MenuItem>
            </FilterSelect>
          </Box>
        </FilterContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Shift No</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Worker's Name</StyledTableCell>
                <StyledTableCell>Position</StyledTableCell>
                <StyledTableCell>Start Time</StyledTableCell>
                <StyledTableCell>End Time</StyledTableCell>
                <StyledTableCell>Total Hours</StyledTableCell>
                <StyledTableCell>Sleepover</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={8} align="center">Loading...</StyledTableCell>
                </StyledTableRow>
              ) : history.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={8} align="center">No shifts found.</StyledTableCell>
                </StyledTableRow>
              ) : (
                history.map((shift: any) => (
                  <StyledTableRow
                    key={shift.shift_id}
                    onClick={async () => {
                      setSelectedShift(shift);
                      setIsModalOpen(true);
                      try {
                        const result = await dispatch(fetchShiftDetails(shift.shift_id)).unwrap();
                        setSelectedShift(prev => ({ ...prev, ...result } as any));
                      } catch (e) {
                        console.error("Failed to fetch full shift details", e);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <StyledTableCell>{shift.shift_id}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.start_date_time).format('DD/MM/YYYY')}</StyledTableCell>
                    <StyledTableCell>
                      {shift.worker ? `${shift.worker.user.first_name} ${shift.worker.user.last_name}` : '-'}
                    </StyledTableCell>
                    <StyledTableCell>{shift.position_role || 'PCA'}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.shift_start_date_time).format('hh:mm')}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.shift_end_date_time).format('hh:mm')}</StyledTableCell>
                    <StyledTableCell>{calculateDuration(shift.shift_start_date_time, shift.shift_end_date_time)}</StyledTableCell>
                    <StyledTableCell>{shift.is_sleepover ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>
                      <StatusChip label={shift.status || 'Completed'} status={shift.status?.toLowerCase() || 'completed'} />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <PaginationContainer>
          <Box sx={{ color: '#666', fontSize: '0.875rem' }}>
            Showing {history.length > 0 ? (page - 1) * pageSize + 1 : 0} of {count} results
          </Box>
          <Pagination
            count={Math.ceil(count / pageSize)}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />
        </PaginationContainer>
      </TableWrapper>

      {selectedShift && (
        <DetailModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          shiftId={selectedShift.shift_id}
          shiftStatus={selectedShift.status}
        >
          {selectedShift.worker && (
            <>
              <WorkerAssigned
                name={`${selectedShift.worker.user.first_name} ${selectedShift.worker.user.last_name}`}
                workerId={selectedShift.worker.worker_id}
                role={selectedShift.position_role || 'Worker'}
                avatarUrl={selectedShift.worker.user.profile_picture}
              />
              <Divider variant="middle" />
            </>
          )}

          <ShiftDetails
            shiftCreated={dayjs(selectedShift.shift_created_on).format('DD/MM/YYYY')}
            shiftAccepted={dayjs(selectedShift.created_on).format('DD/MM/YYYY')}
            scheduledDate={dayjs(selectedShift.shift_start_date_time).format('DD/MM/YYYY')}
            scheduledTime={`${dayjs(selectedShift.shift_start_date_time).format('HH.mm')} - ${dayjs(selectedShift.shift_end_date_time).format('HH.mm')}`}
            experience={`${selectedShift.experience_required || 0}+ years`}
            fullNoOfWorkers={`${selectedShift.shift_assignee_count}/${selectedShift.workers_required}`}
            appointMember={selectedShift.attendance_manager ? `${selectedShift.attendance_manager.first_name} ${selectedShift.attendance_manager.last_name}, Nursing Supervisor` : 'Jobin Abraham, Nursing Supervisor'}
            clockIn={(selectedShift.clock_in_time) ? dayjs(selectedShift.clock_in_time).format('HH:mm') : '-'}
            clockOut={(selectedShift.clock_out_time) ? dayjs(selectedShift.clock_out_time).format('HH:mm') : '-'}
            totalTimeWorked={`${Number(selectedShift.actual_hours_worked || 0).toFixed(2)} hours`}
            compliantStatus={selectedShift.attendance_status || 'Compliant'}
            payRate={selectedShift.invoice_amount}
            workerSuggestedTime={selectedShift.worker_suggested_time || null}
            remarks={selectedShift.remarks || null}
            isSleepover={selectedShift.is_sleepover}
            label={selectedShift.label}
            />
          <Divider variant="middle" />
          <Feedback
            rating={selectedShift.review_rating || 0}
            comment={selectedShift.review_remarks || "No feedback provided."}
          />
        </DetailModal>
      )}
    </Box>
  );
};

export default ShiftHistory;

